/**
 * Industry Nine product builder — one parent runtime.
 *
 * Wheel, hub, and stem share this file for Alpine helpers, scroll, store,
 * and kind profiles. Kind files only keep data wiring (rules, variants).
 *
 * Load this classic script before wheelBuilder.js or hubBuilder.js.
 * Theme assets cannot import each other by relative path (CDN hashes),
 * so this lives on window.I9Builder.
 */
(function (root) {
  "use strict";

  if (root.I9Builder && root.I9Builder.version) return;

  // ---------------------------------------------------------------------------
  // Kind profiles — Alpine names stay the same on the storefront
  // ---------------------------------------------------------------------------

  const KINDS = { WHEEL: "wheel", HUB: "hub", STEM: "stem" };

  const PROFILES = {
    wheel: {
      kind: KINDS.WHEEL,
      alpineTabs: "wheelBuilderTabs",
      alpineForm: "wheelBuilderForm",
      formGetter: "getActiveFormData",
      stemFlag: false,
    },
    hub: {
      kind: KINDS.HUB,
      alpineTabs: "hubBuilderTabs",
      alpineForm: "hubBuilderForm",
      formGetter: "getFooterActionFormData",
      stemFlag: false,
    },
    stem: {
      kind: KINDS.STEM,
      alpineTabs: "hubBuilderTabs",
      alpineForm: "hubBuilderForm",
      formGetter: "getFooterActionFormData",
      stemFlag: true,
    },
  };

  const ADDTOCART_CHECK_MS = 560;
  const SCROLL_BOTTOM_GROUPS = 3;
  const MOBILE_MAX_WIDTH = 768;

  function detectKind() {
    if (typeof root === "undefined") return KINDS.WHEEL;
    const explicit = root.__BUILDER_KIND__;
    if (explicit === KINDS.WHEEL || explicit === KINDS.HUB || explicit === KINDS.STEM) {
      return explicit;
    }
    if (root.__STEM_BUILDER__) return KINDS.STEM;
    if (typeof document !== "undefined" && document.querySelector('[x-data*="hubBuilderTabs"]')) {
      return KINDS.HUB;
    }
    return KINDS.WHEEL;
  }

  function profile(kind) {
    const key = kind || detectKind();
    return PROFILES[key] || PROFILES.wheel;
  }

  function onAlpineInit(fn) {
    if (root.Alpine) {
      fn();
      return;
    }
    if (typeof document !== "undefined" && document.addEventListener) {
      document.addEventListener("alpine:init", fn);
    }
  }

  // ---------------------------------------------------------------------------
  // Alpine / form helpers
  // ---------------------------------------------------------------------------

  function getAlpineData(el) {
    if (!el) return null;
    if (root.Alpine && typeof root.Alpine.$data === "function") {
      try {
        return root.Alpine.$data(el);
      } catch (_) {}
    }
    if (el._x_dataStack && el._x_dataStack[0]) return el._x_dataStack[0];
    if (el.__x && el.__x.$data) return el.__x.$data;
    return null;
  }

  function findClearableFormData(el) {
    const stack = el && el._x_dataStack;
    if (Array.isArray(stack)) {
      for (const d of stack) {
        if (d && typeof d.clearSelections === "function") return d;
      }
    }
    const d = getAlpineData(el);
    return d && typeof d.clearSelections === "function" ? d : null;
  }

  /**
   * Wheel filters by rules-scope keys. Hub/stem check every completeOptions row
   * (including empty-string vs null). Kind is explicit or detected from the page.
   */
  function computeEveryOptionSelected(formData, kind) {
    if (!formData || !Array.isArray(formData.completeOptions)) return false;
    const k = kind || detectKind();
    if (k === KINDS.HUB || k === KINDS.STEM) {
      if (formData.completeOptions.length === 0) return false;
      return formData.completeOptions.every((option) => (option.value ?? "") !== "");
    }
    const inScopeKeys = formData.availableOptionKeysFromRules;
    const optionsToCheck =
      inScopeKeys && inScopeKeys.size > 0
        ? formData.completeOptions.filter((opt) => inScopeKeys.has(opt.id))
        : formData.completeOptions;
    if (optionsToCheck.length === 0) return false;
    return optionsToCheck.every((option) => option.value !== "");
  }

  function eachSelectableChoiceRadio(formEl, optionId, visit) {
    if (!formEl || !optionId || typeof formEl.querySelector !== "function") return;
    const id = String(optionId);
    let group = null;
    try {
      group = formEl.querySelector(`[data-option-group="${CSS.escape(id)}"]`);
    } catch (_) {
      group = formEl.querySelector(`[data-option-group="${id}"]`);
    }
    if (!group) return;
    const radios = group.querySelectorAll('input[type="radio"][data-value]');
    for (let i = 0; i < radios.length; i++) {
      const input = radios[i];
      if (input.disabled) continue;
      const wrap = input.closest("[data-value]") || input;
      if (wrap.classList.contains("disabled") || wrap.classList.contains("unavailable")) continue;
      if (wrap.style.display === "none") continue;
      try {
        if (root.getComputedStyle(wrap).display === "none") continue;
      } catch (_) {}
      if (visit(input, wrap) === false) return;
    }
  }

  /** 0, 1, or 2 — stops once the group is a real choice (2+ tiles). */
  function countSelectableOptionChoices(formEl, optionId) {
    let count = 0;
    eachSelectableChoiceRadio(formEl, optionId, () => {
      count += 1;
      if (count > 1) return false;
    });
    return count;
  }

  /** Radio value when a group has exactly one leftover choice; otherwise null. */
  function soleSelectableChoiceValue(formEl, optionId) {
    let only = null;
    let count = 0;
    eachSelectableChoiceRadio(formEl, optionId, (input) => {
      count += 1;
      if (count === 1) {
        only =
          (input.getAttribute && input.getAttribute("data-value")) ||
          input.value ||
          null;
        return;
      }
      only = null;
      return false;
    });
    return count === 1 && only != null && String(only).trim() !== "" ? String(only) : null;
  }

  /**
   * Auto-pick is for first-time building only. Edit-from-summary must leave
   * a one-tile field empty so the shopper can open it instead of bouncing
   * straight back to the receipt.
   */
  function isEditingFromSummary(formData) {
    if (!formData) return false;
    if (formData._editingFromSummary) return true;
    try {
      const tabs = typeof formData._getTabsData === "function" ? formData._getTabsData() : null;
      if (tabs && tabs.mobileForceOptions) return true;
    } catch (_) {}
    return false;
  }

  /**
   * Fill every unlocked empty group that has one leftover tile. Walks in
   * order so a just-filled row unlocks the next singleton in the same pass.
   * Returns true if any value was set.
   */
  function autoSelectSoleChoices(formData, formEl, filledIds) {
    if (!formData || !Array.isArray(formData.completeOptions) || !formEl) return false;
    if (isEditingFromSummary(formData)) return false;
    const isDisabled = (id) =>
      typeof formData.isInputGroupDisabled === "function" && formData.isInputGroupDisabled(id);
    let selected = false;
    for (const opt of formData.completeOptions) {
      if (!opt) continue;
      if (isDisabled(opt.id)) continue;
      if (String(opt.value ?? "").trim() !== "") continue;
      const sole = soleSelectableChoiceValue(formEl, opt.id);
      if (!sole) continue;
      opt.value = sole;
      selected = true;
      if (Array.isArray(filledIds) && filledIds.indexOf(opt.id) === -1) {
        filledIds.push(opt.id);
      }
    }
    return selected;
  }

  const AUTO_SELECT_PULSE_MS = 1500;
  const AUTO_SELECT_PULSE_STAGGER_MS = 120;

  function autoSelectCompleteDelayMs() {
    if (typeof window === "undefined") return 0;
    if (window.innerWidth > 768) return 0;
    return AUTO_SELECT_PULSE_MS;
  }

  function uniqueOptionIds(ids, formData) {
    const known = Object.create(null);
    if (formData && Array.isArray(formData.completeOptions)) {
      formData.completeOptions.forEach((row) => {
        if (row && row.id) known[row.id] = true;
      });
    }
    const out = [];
    const seen = Object.create(null);
    (Array.isArray(ids) ? ids : []).forEach((id) => {
      const key = String(id || "");
      if (!key || seen[key]) return;
      if (Object.keys(known).length && !known[key]) return;
      seen[key] = true;
      out.push(key);
    });
    return out;
  }

  function optionGroupLabel(formData, id, groupEl) {
    const opt = Array.isArray(formData && formData.completeOptions)
      ? formData.completeOptions.find((row) => row && row.id === id)
      : null;
    if (opt && String(opt.label || "").trim()) return String(opt.label).trim();
    const labelEl =
      groupEl && groupEl.querySelector
        ? groupEl.querySelector(".wheelbuilder-accordion__label")
        : null;
    const fromDom = labelEl && String(labelEl.textContent || "").trim();
    if (fromDom) return fromDom;
    return String(id || "").replace(/_/g, " ");
  }

  function restartAutoSelectPulseClass(el) {
    if (!el || !el.classList) return;
    el.classList.remove("is-auto-selected");
    const add = () => {
      el.classList.add("is-auto-selected");
    };
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(add));
    } else {
      add();
    }
  }

  function markAutoSelectedOptionGroups(formData, formEl, filledIds) {
    if (!formData) return;
    const ids = uniqueOptionIds(filledIds, formData);
    if (formData._autoSelectPulseTimer) {
      clearTimeout(formData._autoSelectPulseTimer);
      formData._autoSelectPulseTimer = null;
    }
    formData.autoFilledIds = [];
    formData.autoFilledNotice = "";

    const apply = () => {
      if (!ids.length) return;
      const labels = [];
      ids.forEach((id, i) => {
        const group =
          formEl && formEl.querySelector
            ? formEl.querySelector(`[data-option-group="${id}"]`)
            : null;
        if (group && group.style && typeof group.style.setProperty === "function") {
          group.style.setProperty("--pulse-delay", `${i * AUTO_SELECT_PULSE_STAGGER_MS}ms`);
        }
        restartAutoSelectPulseClass(group);
        labels.push(optionGroupLabel(formData, id, group));
      });
      formData.autoFilledIds = ids.slice();
      formData.autoFilledNotice = labels.length
        ? `Auto-selected: ${labels.join(", ")}`
        : "";
      const hold =
        AUTO_SELECT_PULSE_MS +
        Math.max(0, ids.length - 1) * AUTO_SELECT_PULSE_STAGGER_MS +
        80;
      const timer = setTimeout(() => {
        formData.autoFilledIds = [];
        formData.autoFilledNotice = "";
        formData._autoSelectPulseTimer = null;
        ids.forEach((id) => {
          const group =
            formEl && formEl.querySelector
              ? formEl.querySelector(`[data-option-group="${id}"]`)
              : null;
          if (group && group.classList) group.classList.remove("is-auto-selected");
        });
      }, hold);
      if (timer && typeof timer.unref === "function") timer.unref();
      formData._autoSelectPulseTimer = timer;
    };

    if (typeof formData.$nextTick === "function") formData.$nextTick(apply);
    else apply();
  }

  /**
   * Auto-pick singletons, re-paint constraints, repeat. Suppresses
   * handleValueSet so the cascade does not scroll through each row.
   */
  function runSoleChoiceAutoSelect(formData, formEl, repaintFn) {
    if (!formData || formData._applyingSoleChoices) return false;
    if (isEditingFromSummary(formData)) return false;
    formData._applyingSoleChoices = true;
    formData._suppressHandleValueSet = true;
    let selected = false;
    const filledIds = [];
    try {
      let n = 0;
      while (n++ < 8) {
        if (!autoSelectSoleChoices(formData, formEl, filledIds)) break;
        selected = true;
        if (typeof repaintFn === "function") repaintFn();
      }
    } finally {
      formData._applyingSoleChoices = false;
      const release = () => {
        formData._suppressHandleValueSet = false;
      };
      if (typeof formData.$nextTick === "function") formData.$nextTick(release);
      else release();
    }
    if (selected) {
      markAutoSelectedOptionGroups(formData, formEl, filledIds);
    }
    return selected;
  }

  function pickOptionIndexToExpand(opts, formData, formEl) {
    if (!Array.isArray(opts) || !opts.length) return -1;
    const firstUnfilledChangeable = nextChangeableUnfilledIndex(opts, formData, formEl);
    if (firstUnfilledChangeable !== -1) return firstUnfilledChangeable;

    const isDisabled = (id) =>
      typeof formData?.isInputGroupDisabled === "function" && formData.isInputGroupDisabled(id);
    let firstUnfilled = -1;
    let firstChangeable = -1;
    for (let i = 0; i < opts.length; i++) {
      const o = opts[i];
      if (!o) continue;
      if (isDisabled(o.id)) continue;
      const filled = String(o.value || "").trim() !== "";
      if (!filled && firstUnfilled === -1) firstUnfilled = i;
      if (firstChangeable === -1 && countSelectableOptionChoices(formEl, o.id) > 1) {
        firstChangeable = i;
      }
      if (firstUnfilled !== -1 && firstChangeable !== -1) break;
    }
    if (firstUnfilled !== -1) return firstUnfilled;
    if (firstChangeable !== -1) return firstChangeable;
    return 0;
  }

  /** First empty group with 2+ leftover tiles. Auto-filled singles are skipped. */
  function nextChangeableUnfilledIndex(opts, formData, formEl) {
    if (!Array.isArray(opts) || !opts.length) return -1;
    const isDisabled = (id) =>
      typeof formData?.isInputGroupDisabled === "function" && formData.isInputGroupDisabled(id);
    for (let i = 0; i < opts.length; i++) {
      const o = opts[i];
      if (!o) continue;
      if (isDisabled(o.id)) continue;
      if (String(o.value || "").trim() !== "") continue;
      if (countSelectableOptionChoices(formEl, o.id) > 1) return i;
    }
    return -1;
  }

  /**
   * Accordion the shipped auto-scroll should land on after a pick.
   * Building skips auto-filled one-tile rows. Edit-from-summary keeps
   * the immediate next row so a leftover single can be opened.
   */
  function optionGroupIsEligible(formData, opt) {
    if (!opt || !opt.id) return false;
    if (
      typeof formData.isOptionGroupVisibleInList === "function" &&
      !formData.isOptionGroupVisibleInList(opt.id)
    ) {
      return false;
    }
    if (
      typeof formData.isInputGroupDisabled === "function" &&
      formData.isInputGroupDisabled(opt.id)
    ) {
      return false;
    }
    return true;
  }

  function lastEligibleOptionGroup(formData, exceptId) {
    const opts = formData && formData.completeOptions;
    if (!Array.isArray(opts)) return null;
    let last = null;
    for (const opt of opts) {
      if (!opt || opt.id === exceptId) continue;
      if (!optionGroupIsEligible(formData, opt)) continue;
      last = opt;
    }
    return last;
  }

  function nextUnfilledEligibleOption(formData, fromId) {
    const opts = formData && formData.completeOptions;
    if (!Array.isArray(opts) || !opts.length) return null;
    const fromIndex = opts.findIndex((row) => row && row.id === fromId);
    const start = fromIndex >= 0 ? fromIndex + 1 : 0;
    for (let i = start; i < opts.length; i++) {
      const opt = opts[i];
      if (!optionGroupIsEligible(formData, opt)) continue;
      if (String(opt.value || "").trim() !== "") continue;
      return opt;
    }
    return null;
  }

  function openOptionGroup(formData, optionId, openOpts) {
    if (!formData || !optionId) return;
    if (typeof formData.activateOptionGroup === "function") {
      formData.activateOptionGroup(optionId, openOpts || { scroll: false });
      return;
    }
    if (!Array.isArray(formData.completeOptions)) return;
    formData.completeOptions.forEach((row) => {
      if (row) row.expanded = row.id === optionId;
    });
  }

  function ensureOneOptionGroupExpanded(formData, exceptId, openOpts) {
    if (!formData || !Array.isArray(formData.completeOptions)) return;
    const anyOpen = formData.completeOptions.some(
      (opt) => opt && opt.expanded && opt.id !== exceptId
    );
    if (anyOpen) return;
    const nextUnfilled = nextUnfilledEligibleOption(formData, exceptId);
    if (nextUnfilled) {
      openOptionGroup(formData, nextUnfilled.id, openOpts);
      return;
    }
    const fallback = lastEligibleOptionGroup(formData, exceptId);
    if (fallback) {
      openOptionGroup(formData, fallback.id, openOpts);
      return;
    }
    const only = formData.completeOptions.find((opt) => opt && opt.id === exceptId);
    if (only) only.expanded = true;
  }

  function radioFromChoiceClick(event) {
    const t = event && event.target;
    if (!t || typeof t.closest !== "function") return null;
    const radio = t.closest('input[type="radio"]');
    if (radio) return radio;
    const label = t.closest("label");
    if (label) {
      const forId = label.getAttribute("for") || label.htmlFor;
      const doc = label.ownerDocument;
      if (forId && doc) {
        const byId = doc.getElementById(forId);
        if (byId && byId.type === "radio") return byId;
      }
      const nested = label.querySelector('input[type="radio"]');
      if (nested) return nested;
    }
    const wrap = t.closest("[data-value]");
    return wrap ? wrap.querySelector('input[type="radio"]') : null;
  }

  function choiceValueFromEventOrValue(eventOrValue) {
    if (typeof eventOrValue === "string") return String(eventOrValue || "").trim();
    const radio = radioFromChoiceClick(eventOrValue);
    return String(radio?.value || radio?.getAttribute?.("data-value") || "").trim();
  }

  /**
   * Re-tap of the already-picked tile: open the next empty field.
   * First tap of a different tile is ignored so handleValueSet can run.
   */
  function advanceFromFilledOption(formData, optionId, eventOrValue) {
    if (!formData || !optionId) return false;
    if (formData._applyingSoleChoices || formData._suppressHandleValueSet) return false;
    const opt = Array.isArray(formData.completeOptions)
      ? formData.completeOptions.find((row) => row && row.id === optionId)
      : null;
    if (!opt) return false;
    const current = String(opt.value || "").trim();
    if (!current) return false;
    const tapped = choiceValueFromEventOrValue(eventOrValue);
    if (!tapped || tapped !== current) return false;
    const next = nextUnfilledEligibleOption(formData, optionId);
    if (!next) return false;
    openOptionGroup(formData, next.id, { scroll: false });
    if (
      typeof window !== "undefined" &&
      window.innerWidth <= 768 &&
      typeof formData.queueOptionGroupScroll === "function"
    ) {
      formData.queueOptionGroupScroll(next.id);
    }
    return true;
  }

  function toggleOptionGroup(formData, optionId, openOpts) {
    if (!formData || !optionId) return;
    if (
      typeof formData.isInputGroupDisabled === "function" &&
      formData.isInputGroupDisabled(optionId)
    ) {
      return;
    }
    const opt = Array.isArray(formData.completeOptions)
      ? formData.completeOptions.find((row) => row && row.id === optionId)
      : null;
    if (!opt) return;
    if (opt.expanded) {
      opt.expanded = false;
      ensureOneOptionGroupExpanded(formData, optionId, openOpts);
      return;
    }
    openOptionGroup(formData, optionId, openOpts);
  }

  function nextOptionAfterPick(opts, formData, formEl, fromIndex) {
    if (!Array.isArray(opts) || fromIndex < 0) return null;
    const immediate = fromIndex + 1 < opts.length ? opts[fromIndex + 1] : null;
    if (isEditingFromSummary(formData)) return immediate;
    const pickIdx = nextChangeableUnfilledIndex(opts, formData, formEl);
    if (pickIdx > fromIndex) return opts[pickIdx] || null;
    if (immediate && String(immediate.value || "").trim() !== "") return null;
    return immediate;
  }

  // ---------------------------------------------------------------------------
  // Viewport / sheet / panel
  // ---------------------------------------------------------------------------

  /**
   * Sliding tab underline. Skip 0-size rects — summary hides the nav
   * (`display:none`), and writing 0px width leaves the selected tab
   * with no bar after the user comes back.
   */
  function applyTabListIndicator(list, tab) {
    if (!list || !tab) return false;
    if (typeof list.getBoundingClientRect !== "function") return false;
    if (typeof tab.getBoundingClientRect !== "function") return false;
    const tabRect = tab.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    if (!(tabRect.width > 1) || !(listRect.width > 1)) return false;
    const scrollLeft = typeof list.scrollLeft === "number" ? list.scrollLeft : 0;
    const offset = tabRect.left - listRect.left + scrollLeft;
    if (!list.style || typeof list.style.setProperty !== "function") return false;
    list.style.setProperty("--wb-tab-indicator-width", `${tabRect.width}px`);
    list.style.setProperty("--wb-tab-indicator-offset", `${offset}px`);
    return true;
  }

  function getVisibleSummarySheet() {
    if (typeof document === "undefined") return null;
    const sheets = document.querySelectorAll(".wheelbuilder-mobile-summary-sheet");
    for (const el of sheets) {
      try {
        const style = root.getComputedStyle(el);
        if (style.display === "none" || style.visibility === "hidden") continue;
      } catch (_) {}
      return el;
    }
    return null;
  }

  /** Prefer Alpine flag OR width — they can disagree (DevTools, delayed resize). */
  function isMobileViewport(tabsData) {
    if (typeof root === "undefined" || typeof root.innerWidth !== "number") return false;
    const byWidth = root.innerWidth <= MOBILE_MAX_WIDTH;
    if (tabsData && typeof tabsData.isMobileViewport === "boolean") {
      return Boolean(tabsData.isMobileViewport) || byWidth;
    }
    return byWidth;
  }

  /**
   * Wheel treats an open options drawer as wizard-active (tablet). Hub/stem do not —
   * single-tab builders set mobileShowOptions on init, which would look like a
   * phone wizard on desktop and leave accordions open next to the summary.
   */
  function mobileWizardActive(tabsData, kind) {
    if (!tabsData) return false;
    const k = kind || detectKind();
    if (k === KINDS.HUB || k === KINDS.STEM) {
      return isMobileViewport(tabsData);
    }
    return isMobileViewport(tabsData) || !!tabsData.mobileShowOptions;
  }

  function prefersReducedMotion() {
    try {
      return root.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (_) {
      return false;
    }
  }

  function readPanelTab(formEl) {
    if (!formEl) return "";
    const el =
      typeof formEl.closest === "function"
        ? formEl.closest("[data-wb-panel-tab]") || formEl
        : formEl;
    if (!el || typeof el.getAttribute !== "function") return "";
    const a = el.getAttribute("data-wb-panel-tab");
    if (a != null && String(a).trim() !== "") return String(a).trim();
    const d = el.dataset && el.dataset.wbPanelTab;
    return d != null && String(d).trim() !== "" ? String(d).trim() : "";
  }

  function resolveOptionsScrollContainer(drawerEl) {
    if (!drawerEl) return null;
    return drawerEl;
  }

  function productImageUrl(product) {
    if (!product) return "";
    if (typeof product.featured_image === "string" && product.featured_image) {
      return product.featured_image;
    }
    if (product.featured_image && product.featured_image.src) return product.featured_image.src;
    if (Array.isArray(product.images) && product.images.length > 0) {
      const img = product.images[0];
      return typeof img === "string" ? img : (img && img.src) || "";
    }
    return "";
  }

  function isMobileCartAddedEnabled() {
    if (typeof root === "undefined" || typeof root.innerWidth !== "number") return false;
    return root.innerWidth <= MOBILE_MAX_WIDTH;
  }

  // ---------------------------------------------------------------------------
  // Option-group scroll metrics (desktop scrollIntoView block)
  // ---------------------------------------------------------------------------

  function optionGroupListMetrics(element) {
    if (!element) return { n: 0, fromIndex: -1, fromEnd: 999 };
    let groups;
    const optionGroupsRoot = element.closest
      ? element.closest(".wheelbuilder-option-groups")
      : null;
    if (optionGroupsRoot) {
      groups = optionGroupsRoot.querySelectorAll("[data-option-group]");
    } else {
      const panel = element.closest ? element.closest("section[role=tabpanel]") : null;
      if (!panel) return { n: 0, fromIndex: -1, fromEnd: 999 };
      groups = panel.querySelectorAll("[data-option-group]");
    }
    if (groups.length < 1) return { n: 0, fromIndex: -1, fromEnd: 999 };
    const i = Array.prototype.indexOf.call(groups, element);
    if (i < 0) return { n: groups.length, fromIndex: -1, fromEnd: 999 };
    return { n: groups.length, fromIndex: i, fromEnd: groups.length - 1 - i };
  }

  function optionGroupFromEnd(element) {
    return optionGroupListMetrics(element).fromEnd;
  }

  function optionGroupScrollBlockFromMetrics(m) {
    if (!m || m.n < 1 || m.fromIndex < 0) return "center";
    if (m.fromEnd <= 1) return "end";
    if (m.n <= 4) {
      return m.fromEnd <= 3 ? "nearest" : "center";
    }
    const p = m.n > 1 ? m.fromIndex / (m.n - 1) : 0;
    if (m.fromEnd <= 3) return "nearest";
    if (m.n >= 6 && p > 0.4) return "nearest";
    if (p >= 0.5) return "nearest";
    return "center";
  }

  function optionGroupScrollIntoViewBlock(element) {
    return optionGroupScrollBlockFromMetrics(optionGroupListMetrics(element));
  }

  // ---------------------------------------------------------------------------
  // Alpine registration
  // ---------------------------------------------------------------------------

  function registerAlpine(name, component) {
    const Alpine = root.Alpine;
    if (Alpine) {
      Alpine.data(name, component);
      return;
    }
    if (typeof document !== "undefined" && document.addEventListener) {
      document.addEventListener("alpine:init", () => {
        root.Alpine.data(name, component);
      });
    }
  }

  function registerStore() {
    const init = () => {
      if (!root.Alpine || typeof root.Alpine.store !== "function") return;
      try {
        if (root.Alpine.store("wheelbuilder")) return;
      } catch (_) {}
      root.Alpine.store("wheelbuilder", {
        showMobileFooter: true,
        showSummaryPage: false,
        isMobileViewport: typeof root.innerWidth === "number" && root.innerWidth <= MOBILE_MAX_WIDTH,
        derivedWheelOptions: null,
        derivedRimOptions: null,
        getKeysForTab(tab) {
          const wheel = this.derivedWheelOptions || {};
          const rim = this.derivedRimOptions || {};
          const canonicalWheel = [
            "spoke_type",
            "hub_model",
            "front_rim",
            "rear_rim",
            "front_spacing",
            "rear_spacing",
            "brake_mount",
            "freehub_driver",
            "hub_color",
            "spoke_color",
          ];
          const canonicalRim = ["rim_size", "hole_count"];
          const wheelKeys = canonicalWheel.filter((k) => k in wheel);
          const frontKeys = canonicalWheel.filter(
            (k) => k in wheel && !["rear_rim", "rear_spacing", "freehub_driver"].includes(k)
          );
          const rearKeys = canonicalWheel.filter(
            (k) => k in wheel && !["front_rim", "front_spacing"].includes(k)
          );
          const rimKeys = canonicalRim.filter((k) => k in rim);
          if (tab === "rim") return rimKeys;
          if (tab === "front") return frontKeys;
          if (tab === "rear") return rearKeys;
          return wheelKeys;
        },
        getDerivedOptionsForTab(tab) {
          return tab === "rim" ? this.derivedRimOptions || {} : this.derivedWheelOptions || {};
        },
      });
    };
    onAlpineInit(init);
  }

  function summaryDesktopFit() {
    return {
      _rAF: null,
      _ro: null,
      _onResize: null,
      _onProgress: null,
      init() {
        const schedule = () => {
          if (this._rAF) return;
          this._rAF = requestAnimationFrame(() => {
            this._rAF = null;
            this.$nextTick(() => {
              this._fit();
            });
          });
        };
        this._onResize = schedule;
        this._onProgress = schedule;
        root.addEventListener("resize", this._onResize, { passive: true });
        if (typeof document !== "undefined") {
          document.addEventListener("wheelbuilder:update-progress", this._onProgress);
        }
        const inner = this.$refs && this.$refs.wbSummaryFitInner;
        if (inner && typeof ResizeObserver !== "undefined") {
          this._ro = new ResizeObserver(this._onProgress);
          this._ro.observe(inner);
        }
        schedule();
      },
      _clearFitStyles() {
        const el = this.$el;
        const inner = this.$refs && this.$refs.wbSummaryFitInner;
        if (el) {
          el.style.removeProperty("height");
          el.style.removeProperty("--wb-summary-avail-h");
        }
        if (inner) {
          inner.style.removeProperty("transform");
          inner.style.removeProperty("transform-origin");
          inner.style.removeProperty("will-change");
          inner.style.removeProperty("width");
          inner.style.removeProperty("max-width");
          const shell = inner.querySelector(".wheelbuilder-summary-desktop-shell");
          if (shell) shell.style.removeProperty("--wb-summary-density");
        }
      },
      _fit() {
        const el = this.$el;
        const inner = this.$refs && this.$refs.wbSummaryFitInner;
        if (!el || !inner) return;
        if (root.innerWidth < MOBILE_MAX_WIDTH) {
          this._clearFitStyles();
          return;
        }
        if (el.offsetWidth < 2) return;

        // Soft density only — never nest-scroll the card, never scale the
        // masthead. Floor ~0.86 so type stays readable; leftover height is
        // reached by normal page scroll down to Add to cart.
        el.style.removeProperty("height");
        el.style.removeProperty("--wb-summary-avail-h");
        inner.style.removeProperty("transform");
        inner.style.removeProperty("transform-origin");
        inner.style.removeProperty("will-change");
        inner.style.width = "100%";
        inner.style.removeProperty("max-width");

        const shell = inner.querySelector(".wheelbuilder-summary-desktop-shell");
        if (shell) shell.style.removeProperty("--wb-summary-density");

        const title = document.querySelector(".wheelbuilder-top-header");
        const titleBottom = title ? title.getBoundingClientRect().bottom : 0;
        const topReserve = 48;
        const bottomReserve = 72;
        let availH = root.innerHeight - Math.max(0, titleBottom) - topReserve - bottomReserve;
        const vvh = root.visualViewport && root.visualViewport.height;
        if (vvh) {
          availH = Math.min(availH, vvh - topReserve - bottomReserve);
        }
        if (!Number.isFinite(availH) || availH < 120) {
          if (shell) shell.style.setProperty("--wb-summary-density", "1");
          return;
        }

        const naturalH = Math.ceil(inner.getBoundingClientRect().height);
        if (naturalH < 2) return;

        // Mild compress only. Floor 0.86 keeps the card readable; page scroll
        // covers anything still below the fold (Add to cart).
        const DENSITY_FLOOR = 0.86;
        let density = 1;
        if (naturalH > availH) {
          density = Math.max(DENSITY_FLOOR, availH / naturalH);
        }
        density = Math.round(density * 1000) / 1000;
        if (shell) {
          shell.style.setProperty("--wb-summary-density", String(density));
        }
      },
    };
  }

  function roundCents(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n);
  }

  /**
   * Full price for one part. Was price counts only when it is strictly higher
   * than the pay price. Equal, missing, or lower is not a discount.
   */
  function partFullCents(payCents, wasCents) {
    const pay = roundCents(payCents);
    const was = roundCents(wasCents);
    return was > pay ? was : pay;
  }

  /**
   * @param {{ payCents: number, wasCents: number }[]} parts
   * @returns {{ payCents: number, fullCents: number, compareAtCents: number|null }}
   */
  function fullPriceCents(parts) {
    let pay = 0;
    let full = 0;
    for (const part of parts || []) {
      const payCents = roundCents(part && part.payCents);
      const wasCents = roundCents(part && part.wasCents);
      pay += payCents;
      full += partFullCents(payCents, wasCents);
    }
    return {
      payCents: pay,
      fullCents: full,
      compareAtCents: full > pay ? full : null,
    };
  }

  /** Hidden cart property value, or null when there is nothing to strike. */
  function compareAtPropertyCents(payCents, wasCents) {
    const pay = roundCents(payCents);
    const was = roundCents(wasCents);
    if (was > pay) return String(was);
    return null;
  }

  /**
   * B2B was-price: session compare-at when Shopify already has one
   * (same number the cart strikes), else Storefront market retail.
   */
  function b2bWasCents(sessionCompareAt, retailCents) {
    const sessionWas = roundCents(sessionCompareAt);
    if (Number.isFinite(sessionWas) && sessionWas > 0) return sessionWas;
    const retail = roundCents(retailCents);
    return Number.isFinite(retail) ? retail : 0;
  }

  /**
   * ISO-3166 alpha-2 only. Empty / junk → null so B2B retail does not
   * silently query the US market (wrong compare-at vs a CA company).
   */
  function normalizeRetailCountry(value) {
    const code = String(value || "").trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : null;
  }

  /**
   * Pick the market-retail cents for a variant from a Storefront quote list.
   * A currency mismatch returns null so the builder shows the pay price only.
   */
  function matchRetailCents(quotes, variantId, expectedCurrency) {
    if (!Array.isArray(quotes) || quotes.length === 0) return null;
    const id = String(variantId || "");
    const row = quotes.find((quote) => id && String(quote && quote.id || "").endsWith("/" + id));
    if (!row) return null;
    const expected = String(expectedCurrency || "").toUpperCase();
    const currency = String(row.currency || "").toUpperCase();
    if (!expected || !currency || currency !== expected) return null;
    const cents = roundCents(row.cents);
    return Number.isFinite(cents) ? cents : null;
  }

  const WHEEL_PARENT_TAB_TITLES = {
    complete: "Complete",
    front: "Front",
    rear: "Rear",
    rim: "Rim",
  };

  const HUB_PARENT_TAB_TITLES = {
    complete: "Hubset",
    front: "Front hub",
    rear: "Rear hub",
  };

  const STEM_PARENT_TAB_TITLES = {
    stem: "Stem",
  };

  function wheelParentTabKey(tab) {
    const key = String(tab || "").trim().toLowerCase();
    if (key === "wheelset") return "complete";
    return Object.prototype.hasOwnProperty.call(WHEEL_PARENT_TAB_TITLES, key) ? key : "";
  }

  function parentTabTitles(kind) {
    if (kind === KINDS.HUB) return HUB_PARENT_TAB_TITLES;
    if (kind === KINDS.STEM) return STEM_PARENT_TAB_TITLES;
    return WHEEL_PARENT_TAB_TITLES;
  }

  function parentTabKey(tab, kind) {
    if (kind === KINDS.STEM) {
      const key = String(tab || "").trim().toLowerCase();
      return key === "stem" || key === "" ? "stem" : "";
    }
    return wheelParentTabKey(tab);
  }

  function detectParentKindFromVariants(variants) {
    const titles = new Set();
    for (const variant of Array.isArray(variants) ? variants : []) {
      const title = String(variant && variant.title || "").trim();
      if (title) titles.add(title);
    }
    const hasAll = (needed) => needed.every((title) => titles.has(title));
    if (hasAll(Object.values(WHEEL_PARENT_TAB_TITLES))) return KINDS.WHEEL;
    if (hasAll(Object.values(HUB_PARENT_TAB_TITLES))) return KINDS.HUB;
    if (hasAll(Object.values(STEM_PARENT_TAB_TITLES))) return KINDS.STEM;
    return "";
  }

  function parentAvailabilityFromVariants(variants, kind) {
    const titles = parentTabTitles(kind);
    const byTitle = new Map();
    for (const variant of Array.isArray(variants) ? variants : []) {
      const title = String(variant && variant.title || "").trim();
      if (title && !byTitle.has(title)) byTitle.set(title, variant);
    }
    const soldOut = {};
    for (const key of Object.keys(titles)) soldOut[key] = false;
    const active = Object.entries(titles).every(([key, title]) => {
      if (!byTitle.has(title)) return false;
      const policy = String(byTitle.get(title).inventory_policy || "").trim().toLowerCase();
      soldOut[key] = policy !== "continue";
      return true;
    });
    if (!active) {
      for (const key of Object.keys(soldOut)) soldOut[key] = false;
    }
    return { active, soldOut };
  }

  function parentTabSoldOut(availability, tab, kind) {
    if (!availability || availability.active !== true) return false;
    const key = parentTabKey(tab, kind || KINDS.WHEEL);
    return key ? availability.soldOut[key] === true : false;
  }

  function parentCardSoldOut(options) {
    const opts = options || {};
    if (opts.linked && !opts.targetFound) return false;
    const kind = detectParentKindFromVariants(opts.variants);
    if (!kind) return false;
    const availability = parentAvailabilityFromVariants(opts.variants, kind);
    if (!availability.active) return false;
    return Object.keys(parentTabTitles(kind)).every((key) => availability.soldOut[key] === true);
  }

  /**
   * Wheel purchase state comes from the parent variants named Complete,
   * Front, Rear, and Rim. The control stays off until every one exists.
   */
  function wheelParentAvailabilityFromVariants(variants) {
    return parentAvailabilityFromVariants(variants, KINDS.WHEEL);
  }

  function wheelParentTabSoldOut(availability, tab) {
    return parentTabSoldOut(availability, tab, KINDS.WHEEL);
  }

  /**
   * Preset cfg is `key=value;key=value`. A missing tab means Wheelset.
   * An unknown tab is invalid so cards keep their current behavior.
   */
  function wheelBuilderCfgTab(cfg) {
    const raw = String(cfg || "").trim();
    if (!raw) return "complete";
    let sawTab = false;
    let tab = "";
    for (const part of raw.split(";")) {
      const eq = part.indexOf("=");
      if (eq < 0) continue;
      let key = part.slice(0, eq);
      let value = part.slice(eq + 1);
      try {
        key = decodeURIComponent(key);
        value = decodeURIComponent(value);
      } catch (_) {}
      key = key.trim().toLowerCase();
      if (key !== "tab" && key !== "t") continue;
      sawTab = true;
      tab = value.trim().toLowerCase();
    }
    if (!sawTab) return "complete";
    return wheelParentTabKey(tab) || null;
  }

  /**
   * Dummy and direct cards share one rule: sold out only when every parent
   * tab variant refuses continue-selling. The cfg tab does not change it.
   */
  function wheelCardSoldOut(options) {
    const opts = options || {};
    if (opts.linked && !opts.targetFound) return false;
    const availability = parentAvailabilityFromVariants(opts.variants, KINDS.WHEEL);
    if (!availability.active) return false;
    return Object.keys(WHEEL_PARENT_TAB_TITLES).every((key) => availability.soldOut[key] === true);
  }

  /** Percent off the struck price. Null when there is no real discount. */
  function savingsOffPercent(payCents, wasCents) {
    const pay = roundCents(payCents);
    const was = roundCents(wasCents);
    if (pay <= 0 || was <= pay) return null;
    const pct = Math.round(((was - pay) / was) * 100);
    return pct > 0 ? pct : null;
  }

  /**
   * iOS 26/27 Chrome parks a liquid-glass toolbar under `position: fixed`.
   * The leftover is the layout viewport below the visual viewport.
   * 0 when the sheet already sits on the visible edge.
   */
  function visualViewportBottomGap(view, layoutHeight) {
    const visual = view && typeof view.height === "number" ? view : null;
    const layout = Number(layoutHeight);
    if (!visual || !Number.isFinite(layout) || layout <= 0) return 0;
    const top = Number.isFinite(visual.offsetTop) ? visual.offsetTop : 0;
    const gap = Math.round(layout - visual.height - top);
    return gap > 1 ? gap : 0;
  }

  function bindMobileDrawerViewport(win) {
    const host = win || root;
    if (!host || !host.document || host.__i9WbViewportBound) return;
    host.__i9WbViewportBound = true;
    const rootEl = host.document.documentElement;
    let lastGap = null;
    const apply = () => {
      const gap = visualViewportBottomGap(host.visualViewport, host.innerHeight);
      if (gap === lastGap) return;
      lastGap = gap;
      rootEl.style.setProperty("--wb-vv-bottom-gap", gap + "px");
    };
    apply();
    host.addEventListener("resize", apply, { passive: true });
    host.addEventListener("orientationchange", apply, { passive: true });
    if (host.visualViewport) {
      host.visualViewport.addEventListener("resize", apply, { passive: true });
    }
  }

  const I9Builder = {
    version: "1",
    KINDS,
    PROFILES,
    ADDTOCART_CHECK_MS,
    SCROLL_BOTTOM_GROUPS,
    partFullCents,
    fullPriceCents,
    compareAtPropertyCents,
    b2bWasCents,
    normalizeRetailCountry,
    matchRetailCents,
    savingsOffPercent,
    parentTabTitles,
    parentAvailabilityFromVariants,
    parentTabSoldOut,
    parentCardSoldOut,
    detectParentKindFromVariants,
    wheelParentTabKey,
    wheelParentAvailabilityFromVariants,
    wheelParentTabSoldOut,
    wheelBuilderCfgTab,
    wheelCardSoldOut,
    visualViewportBottomGap,
    bindMobileDrawerViewport,
    detectKind,
    profile,
    getAlpineData,
    findClearableFormData,
    computeEveryOptionSelected,
    countSelectableOptionChoices,
    soleSelectableChoiceValue,
    isEditingFromSummary,
    autoSelectSoleChoices,
    runSoleChoiceAutoSelect,
    markAutoSelectedOptionGroups,
    AUTO_SELECT_PULSE_MS,
    autoSelectCompleteDelayMs,
    pickOptionIndexToExpand,
    nextChangeableUnfilledIndex,
    nextOptionAfterPick,
    nextUnfilledEligibleOption,
    toggleOptionGroup,
    ensureOneOptionGroupExpanded,
    advanceFromFilledOption,
    applyTabListIndicator,
    getVisibleSummarySheet,
    isMobileViewport,
    mobileWizardActive,
    prefersReducedMotion,
    readPanelTab,
    resolveOptionsScrollContainer,
    productImageUrl,
    isMobileCartAddedEnabled,
    optionGroupListMetrics,
    optionGroupFromEnd,
    optionGroupScrollBlockFromMetrics,
    optionGroupScrollIntoViewBlock,
    registerAlpine,
    registerStore,
    summaryDesktopFit,
  };

  root.I9Builder = I9Builder;
  root.IndustryNineBuilder = I9Builder;
  registerStore();
  registerAlpine("wbSummaryDesktopFit", summaryDesktopFit);
  if (root.document) {
    const start = () => bindMobileDrawerViewport(root);
    if (root.document.readyState === "loading") {
      root.document.addEventListener("DOMContentLoaded", start, { once: true });
    } else {
      start();
    }
  }

  if (typeof module !== "undefined" && module.exports) {
    module.exports = I9Builder;
  }
})(typeof window !== "undefined" ? window : globalThis);
